import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { ManifestRow, ManifestRowId, Narrator, NarratorAction } from '../setupState';
import type { SetupArea } from '../steps';

/**
 * Brand ink, and what it is spent on.
 *
 * ONLY ACTIONS THAT MOVE THE CONTRACT. The narrator's `Open contract` was a brand-filled button
 * that navigated where the app's own nav already goes; it is gone, and with it the last filled
 * button on this surface that did not change the contract's state. What is left wears the brand:
 * get the admin to the gate that is blocking (`Take me there`, `Show me`), chase a signature
 * (`Resend link`), or activate. Everything else keeps the near-black primary or goes ghost.
 */
export const BRAND_BUTTON =
    'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-[hsl(var(--brand)/0.9)]';

export interface NarratorHandlers<
    Area extends string = SetupArea,
    Id extends string = ManifestRowId,
> {
    onJump: (row: ManifestRow<Area, Id>) => void;
    onActivate: () => void;
    onOpenReceipt: () => void;
}

/**
 * IT TAKES THE SENTENCE AND THE ROWS, NOT THE STATE — `Manifest`'s narrowing, for its reason.
 *
 * The band reads `state.narrator` and resolves each action's `rowId` against `state.rows`, and it
 * has never read anything else off the page's derivation. Narrowing the prop to those two members
 * is what lets the advance dossier — which has no `ActivationGate` to hand over — render the same
 * line, and every existing call site passes a `SetupState`, which carries both.
 */
interface Props<Area extends string, Id extends string> extends NarratorHandlers<Area, Id> {
    state: { narrator: Narrator<Area, Id>; rows: ManifestRow<Area, Id>[] };
}

interface ResolvedAction {
    id: string;
    label: string;
    kind: NarratorAction['kind'];
    run: () => void;
}

/**
 * An action the narrator can actually perform, or nothing.
 *
 * NOTHING IN THIS DESIGN IS EVER MUTE, which cuts both ways: a control that cannot do what its
 * label says is worse than no control. The signing link is minted on the agreements canvas, so
 * `Resend link` becomes an honest jump to the row that owns the link, and the secondary
 * `Copy link` is dropped rather than shipped as a button that copies nothing.
 */
function resolveAction<Area extends string, Id extends string>(
    action: NarratorAction<Area, Id>,
    state: { rows: ManifestRow<Area, Id>[] },
    handlers: NarratorHandlers<Area, Id>,
): ResolvedAction | null {
    const row = action.rowId ? state.rows.find((candidate) => candidate.id === action.rowId) : undefined;

    switch (action.intent) {
        case 'jump':
            if (!row) return null;
            return { id: action.id, label: action.label, kind: action.kind, run: () => handlers.onJump(row) };
        case 'activate':
            return { id: action.id, label: action.label, kind: action.kind, run: handlers.onActivate };
        case 'open-receipt':
            return { id: action.id, label: action.label, kind: action.kind, run: handlers.onOpenReceipt };
        case 'resend-link':
            if (!row) return null;
            return {
                id: action.id,
                label: `Open ${row.label.charAt(0).toLowerCase()}${row.label.slice(1)}`,
                kind: action.kind,
                run: () => handlers.onJump(row),
            };
        case 'copy-link':
        default:
            return null;
    }
}

/**
 * One sentence of ground truth, and never more than two things to do about it.
 *
 * THIS IS THE HEADER'S SUBTITLE, not a band of its own. It renders into the `sd-hdr` title block
 * directly under the `text-4xl` identity line, in the `text-muted-foreground` the pricing screens
 * put there — so the one line on this page that changes sits where every dashboard screen puts
 * the line that says what you are looking at, instead of in a second masthead beneath the first.
 *
 * IT IS NEVER WRITTEN BY A HANDLER. Its sentence comes out of `computeSetupState` with
 * everything else on the page, so it cannot describe a state the manifest disagrees with — the
 * failure that made the old screen say "all steps complete" beside a server that refused.
 * `aria-live="polite"` because the sentence changes underneath the reader when a signature
 * lands or a seal finishes, and that is exactly the moment worth announcing; the region is
 * mounted from the first paint, empty sentence or not, because a live region that appears with
 * its own content announces nothing.
 */
export default function NarratorLine<Area extends string, Id extends string>({
    state,
    ...handlers
}: Props<Area, Id>) {
    const actions = state.narrator.actions
        .map((action) => resolveAction(action, state, handlers))
        .filter((action): action is ResolvedAction => !!action)
        .slice(0, 2);

    return (
        <div className="sd-hdr-line">
            <p className="text-muted-foreground" aria-live="polite">
                {state.narrator.sentence}
            </p>
            {actions.length > 0 && (
                <div className="sd-hdr-actions">
                    {actions.map((action) => (
                        <Button
                            key={action.id}
                            size="sm"
                            variant={action.kind === 'primary' ? 'default' : 'ghost'}
                            className={cn('h-8', action.kind === 'primary' && BRAND_BUTTON)}
                            onClick={action.run}
                        >
                            {action.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
}
